import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DialogSubmitFooter} from '@/components/dialog-submit-footer'
import {HelpIcon} from '@/components/help-icon'
import {DataTable, type Column} from '@/components/data-table'
import {wompiMinimumProblem} from '@/components/cartera/amount-iva-fields'
import {DescuentoAtajo} from '@/components/cartera/price-form-controls'
import {
  CARTERA_PAGE_SIZE,
  useSetPriceOverride,
  useScopedPriceBook,
  type PriceBookEntry,
} from '@/hooks/inci/cartera/use-cartera'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {formatCopFull} from '@/lib/money'
import {formatSegmentKey} from '@/lib/segments'
import {translateApiError} from '@/lib/errors'

type Scope = {businessId?: string; partnerId?: string}
type EditTarget = {entry: PriceBookEntry; scope: Scope} | null

/** Guardar usa el mismo POST que crear sobre la misma celda: el backend supersede, nunca edita en sitio. */
function EditPriceDialog({target, onClose, testPrefix}: {target: EditTarget; onClose: () => void; testPrefix: string}) {
  const {t} = useTranslation()
  const setOverride = useSetPriceOverride()
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!target) return
    setError(null)
    setAmount(String(target.entry.amount_cop))
  }, [target])

  function handleOpenChange(open: boolean) {
    if (!open) {
      setError(null)
      setOverride.reset()
      onClose()
    }
  }

  // El IVA no se edita aquí: el mínimo de Wompi se revisa contra el `iva_pct` real de la entrada.
  function amountProblem(amountStr: string): string | null {
    if (!target) return null
    const amountNum = Number(amountStr)
    if (!amountStr.trim() || !Number.isFinite(amountNum) || amountNum < 0) {
      return t('cartera.precios.amountInvalid')
    }
    return wompiMinimumProblem(t, Math.round(amountNum), target.entry.iva_pct)
  }

  function submit() {
    if (!target) return
    const problem = amountProblem(amount)
    if (problem) {
      setError(problem)
      return
    }
    const amountNum = Number(amount)
    setError(null)
    setOverride.mutate(
      {
        item_kind: target.entry.item_kind,
        segment_key: target.entry.segment_key,
        billing_cycle: target.entry.billing_cycle,
        amount_cop: Math.round(amountNum),
        iva_pct: target.entry.iva_pct,
        business_id: target.scope.businessId,
        partner_id: target.scope.partnerId,
      },
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={handleOpenChange}>
      <DialogContent data-testid={`${testPrefix}-edit-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {target &&
              t('cartera.precios.dialogTitle', {
                segment: target.entry.segment_key
                  ? formatSegmentKey(target.entry.segment_key, t)
                  : t('cartera.overrides.segmentoGeneral'),
                cycle: t(`cartera.precios.${target.entry.billing_cycle}`),
              })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {target && (
            <DescuentoAtajo baseAmount={target.entry.amount_cop} onApply={(value) => setAmount(String(value))} />
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`${testPrefix}-edit-amount`}>{t('cartera.precios.amountLabel')}</Label>
            <Input
              id={`${testPrefix}-edit-amount`}
              data-testid={`${testPrefix}-edit-amount`}
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => setError(amount.trim() === '' ? null : amountProblem(amount))}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogSubmitFooter
          onCancel={() => handleOpenChange(false)}
          onSubmit={submit}
          submitLabel={t('cartera.precios.submit')}
          pending={setOverride.isPending}
          testId={`${testPrefix}-edit-submit`}
        />
      </DialogContent>
    </Dialog>
  )
}

export function ScopedPriceTable({scope, testPrefix}: {scope: Scope; testPrefix: string}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  // «Solo lo que rige hoy» lo filtra el servidor (`vigente=true`), no una página ya cortada.
  const {data, isLoading} = useScopedPriceBook(scope, page)
  const [editTarget, setEditTarget] = useState<EditTarget>(null)

  const rows = data?.items ?? []

  const columns: Column<PriceBookEntry>[] = [
    {
      key: 'segment',
      header: t('cartera.precios.segment'),
      cell: (row) => (
        <span className="text-sm text-foreground">
          {row.segment_key ? formatSegmentKey(row.segment_key, t) : t('cartera.overrides.segmentoGeneral')}
        </span>
      ),
    },
    {
      key: 'cycle',
      header: t('cartera.overrides.cicloColumn'),
      cell: (row) => t(`cartera.precios.${row.billing_cycle}`),
    },
    {
      key: 'amount',
      // U20: el tipo del encabezado va en el `headerAside` de esta columna, nunca en `columnGroups`.
      header: t('cartera.precios.amountColumn'),
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('cartera.precios.amountColumn')})}
          testId="cartera-precios-amount-help">
          {t('cartera.precios.amountColumnHelp')}
        </HelpIcon>
      ),
      className: 'text-right',
      cell: (row) => (
        <span className="font-mono text-sm tabular-nums text-foreground">{formatCopFull(row.amount_cop)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (row) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`${testPrefix}-editar-${row.id}`}
          onClick={() => setEditTarget({entry: row, scope})}>
          {t('cartera.overrides.editar')}
        </Button>
      ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-24 w-full" />

  return (
    <div className="space-y-2" data-testid={`${testPrefix}-existentes`}>
      <h3 className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('cartera.overrides.existentesTitulo')}
      </h3>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyState={
          // Vive siempre dentro de una tarjeta: un borde punteado sería un segundo canto.
          <p className="px-6 py-8 text-center text-sm text-muted-foreground">{t('cartera.overrides.sinOverrides')}</p>
        }
      />
      {!!data?.total && (
        <PaginationBar page={page} pageSize={CARTERA_PAGE_SIZE} total={data.total} onPageChange={setPage} />
      )}
      <EditPriceDialog target={editTarget} onClose={() => setEditTarget(null)} testPrefix={testPrefix} />
    </div>
  )
}
